/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import isNil from 'lodash.isnil';
import {createSelector} from 'reselect';
import {createDeepEqualSelector} from '@ion/applications/src/store/selectors/SelectorUtils';
import {AccountHashMapKeyType} from '@ion/core/src/entities/Accounts';
import Device from '@ion/core/src/services/Device';
import {EventCode} from '@ion/core/src/services/EventCodes';
import {GlobalSearchResult, GlobalSearchResults} from '@ion/core/src/services/GlobalSearch';
import {
  AugmentedSmarterTrailerSensorData,
  CVSAssetAggregations,
  CVSAssetType,
  DateFilterOptions,
  Device as DeviceEntity,
  GenericObject,
  GeoFenceStatus,
  HVSRecord,
  SCDeviceType,
  TagReportType,
  ToolAssetType
} from '@ion/core/src/types/entities';
import {isArrayEmpty} from '@ion/core/src/utils/ArrayUtil';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {SavedPlacesProps} from '@ion/dls/src/maps/geofences/SearchAddress';
import {
  BaseMapEnum,
  BoundaryFilterType,
  DateSelectorValues,
  FilterItems,
  HistoryInputEvents,
  HistoryReportTypeEnum,
  LabelContentType,
  MapLayerSettings,
  MapLayerTypeEnum,
  SavedMapViewType, SideSheetView,
  TimeModes,
  TrackingFilters,
  TrackingMode
} from '@ion/dls/src/types/types';
import {QueryReducerState, TrackingReducerState} from '@/store/types';
import {AssetSelectionType, FindNearby, FindNearbyV2} from '../reducers/tracking';
import {selectDesktopSideSheetOpen, selectMobileSideSheetOpen} from './nav-selectors';

// input selectors ...
const selectIsInitialized           = ({tracking: {isInitialized}}): boolean => isInitialized;
const selectIsFMAssetInitialized    = ({tracking: {isFMAssetInitialized}}): boolean => isFMAssetInitialized;
const selectIsGeofenceLoaded        = ({tracking: {isGeofenceLoaded}}): boolean => isGeofenceLoaded;
const selectProcessedFilters        = ({tracking}: TrackingReducerState) => tracking.processedFilters;
const selectSideSheetView           = ({tracking}: TrackingReducerState) => tracking.sideSheetView;
const selectIsMapOnlyView           = ({tracking}: TrackingReducerState) => tracking.isMapOnlyView;
const selectFilters                 = ({tracking: {filters}}: TrackingReducerState) => filters;
const selectFilterType              = (
  types: string) => (filters: TrackingFilters): FilterItems | DateFilterOptions | BoundaryFilterType => filters[types];
const selectSearchResults           = ({tracking: {search: {results}}}: TrackingReducerState) => results;
const selectSearchHistory           = ({tracking: {search: {history}}}: TrackingReducerState) => history;
const selectSearchLoading           = ({tracking: {search: {loading}}}: TrackingReducerState) => loading;
const selectSearchSelected          = ({tracking: {search: {selected}}}: TrackingReducerState) => selected;
const selectFindNearbyV2            = ({tracking: {findNearbyV2}}: TrackingReducerState) => findNearbyV2;
const selectFindNearby              = ({tracking: {findNearby}}: TrackingReducerState) => findNearby;
const selectFindNearbyAssets        = ({tracking: {findNearby}}: TrackingReducerState) => findNearby.results;
const selectShowPopup               = ({tracking: {findNearbyState: {showPopup}}}: TrackingReducerState) => showPopup;
const selectFindNearbyToggle        = ({tracking: {findNearbyState: {toggle}}}: TrackingReducerState) => toggle;
const selectIsMobileFilterActive    = ({tracking: {isMobileFilterActive}}) => isMobileFilterActive;
const selectMapLayers               = ({tracking: {maps}}: TrackingReducerState) => maps.layers;
const selectAssets                  = ({tracking: {assets}}: TrackingReducerState) => assets;
const selectAssetsList              = ({tracking: {assets: {assets: assetsList}}}: TrackingReducerState) => assetsList;
const selectIsAsstesLoading         = ({tracking: {assets: {loading}}}: TrackingReducerState) => loading;
const selectIsAsstesCountLoading    = ({tracking: {assets: {countLoading}}}: TrackingReducerState) => countLoading;
const selectFilteredAssets          = ({tracking: {assets: {filteredAssets}}}: TrackingReducerState) => filteredAssets;
const selectFilteredDevices        = ({tracking: {assets: {filteredDevices}}}: TrackingReducerState) => filteredDevices;
const selectFindButtonState         = ({tracking: {findButtonState}}): boolean => findButtonState;
const selectFleetMode               = ({tracking: {fleetMode}}) => fleetMode;
const selectFindNearbyState         = ({tracking: {findNearbyState}}) => findNearbyState;
const selectMaps                    = ({tracking: {maps}}) => maps;
const selectToolPanelOpen           = ({tracking: {tools}}: TrackingReducerState) => tools.toolPanelOpen;
const selectAssetDetailView         = ({tracking: {assetDetailView}}: TrackingReducerState) => assetDetailView;
const selectTrackingPersistView     = ({tracking: {persistView}}: TrackingReducerState) => persistView;
const selectAccountMap              = ({tracking: {accountMap}}: TrackingReducerState) => accountMap;
const selectTrafficSubLayers        = ({tracking: {maps}}: TrackingReducerState) => maps.trafficSubLayers;
const selectTrafficOpacity          = ({tracking: {maps}}: TrackingReducerState) => maps.trafficOpacity;
const selectWeatherOpacity          = ({tracking: {maps}}: TrackingReducerState) => maps.weatherOpacity;
const selectGeofenceOpacity         = ({tracking: {maps}}: TrackingReducerState) => maps.geofenceOpacity;
const selectGeofenceBorderOnly      = ({tracking: {geofences}}: TrackingReducerState) => geofences.borderOnly;
const selectGeofenceLabel           = ({tracking: {geofences}}: TrackingReducerState) => geofences.geofenceLable;
const selectSelectedAsset           = ({tracking: {assets}}: TrackingReducerState) => assets.selectedAsset;
const selectLastSelectedAsset       = ({tracking: {assets}}: TrackingReducerState) => assets.lastSelectedAsset;
const selectPanelTopPosition        = ({tracking: {assets}}: TrackingReducerState) => assets.panelTopPosition;
const selectIsAutoRefresh           = ({tracking: {assets}}: TrackingReducerState) => assets.isAutoRefresh;
const selectHasGridUpdate           = ({tracking: {grid}}: TrackingReducerState) => grid.hasUpdate;
const selectIsClusteringActive      = ({tracking: {assets}}: TrackingReducerState) => assets.clustering;
const selectIsMapClusteringActive   = ({tracking: {assets}}: TrackingReducerState) => assets.mapClustering;
const selectIsLabellingActive       = ({tracking: {assets}}: TrackingReducerState) => assets.labelling;
const selectAssetLabelContent       = ({tracking: {assets}}: TrackingReducerState) => assets.labelContent;
const selectSelectedTool            = ({tracking: {tools}}: TrackingReducerState) => tools.selectedTool;
const selectToolDetailView          = ({tracking}: TrackingReducerState) => tracking.toolDetailView;
const selectIsMapSelection          = ({tracking}: TrackingReducerState) => tracking.isMapSelection;
const selectPanMapOnly              = ({tracking}: TrackingReducerState) => tracking.panMapOnly;
const selectMapAssets               = ({tracking: {assets}}: TrackingReducerState) => assets.assets;
const selectSelectedGeoFences       = ({tracking: {filters: {geoFences}}}: TrackingReducerState) => geoFences.selected;
const selectSavingGeofence          = ({tracking: {geofences}}: TrackingReducerState) => geofences.savingGeofence;
const selectSavedGeofence           = ({tracking: {geofences}}: TrackingReducerState) => geofences.savedGeofence;
const selectAssetsHistory           = ({tracking: {history}}: TrackingReducerState) => history.assets;
const selectHistoryIntervalInHours  = ({tracking: {history}}: TrackingReducerState) => history.intervalInHours;
const selectAssetsHistoryLoading    = ({tracking: {history}}: TrackingReducerState) => history.loading;
const selectHistoryUpdatesLive      = ({tracking: {history}}: TrackingReducerState) => history.updatesLive;
const selectHistoryReportType       = ({tracking: {history}}: TrackingReducerState) => history.reportType;
const selectHistoryInputEvents      = ({tracking: {history}}: TrackingReducerState) => history.inputEvents;
const selectSelectedHistoryAsset    = ({tracking: {history}}: TrackingReducerState) => history.selectedAsset;
const selectShowHistoryError        = ({tracking: {history: {error}}}: TrackingReducerState) => error.show;
const selectHistoryAssetCount       = ({tracking: {history: {error}}}: TrackingReducerState) => error.assetCount;
const selectIsHistoryRefresh        = ({tracking: {history}}: TrackingReducerState) => history.isRefresh;
const selectGeofenceStatus          = ({tracking: {geofences}}: TrackingReducerState) => geofences.geofenceStatus;
const selectInitialized             = ({tracking: {maps}}: TrackingReducerState) => maps.initialized;
const selectIsAssetDetailHistoryMap = ({tracking: {maps}}: TrackingReducerState) => maps.isAssetDetailHistory;
const selectBasemap                 = ({tracking: {maps}}: TrackingReducerState) => maps.basemap;
const selectZoom                    = ({tracking: {maps}}: TrackingReducerState) => maps.zoom;
const selectView                    = ({tracking: {maps}}: TrackingReducerState) => maps.view;
const selectInitialExtent           = ({tracking: {maps}}: TrackingReducerState) => maps.initialExtent;
const selectMapviewInitialExtent    = ({tracking: {maps}}: TrackingReducerState) => maps.extents.initialExtent;
const selectMapviewExtent           = ({tracking: {maps}}: TrackingReducerState) => maps.extents.extent;
const selectExtent                  = ({tracking: {maps}}: TrackingReducerState) => maps.extent;
const selectCenter                  = ({tracking: {maps}}: TrackingReducerState) => maps.center;
const selectOperationalLayers       = ({tracking: {maps}}: TrackingReducerState) => maps.operationalLayers;
const selectIsLayerListUpdate       = ({tracking: {maps}}: TrackingReducerState) => maps.isLayerListUpdate;
const selectIslayerVisibility       = ({tracking: {maps}}: TrackingReducerState) => maps.islayerVisibilityUpdate;
const selectMapLayerType            = ({tracking: {maps}}: TrackingReducerState) => maps.mapLayerType;
const selectMapLayerSettings        = ({tracking: {maps}}: TrackingReducerState) => maps.mapLayerSettings;
const selectHistorySearch           = ({tracking}: TrackingReducerState) => tracking.searchHistory;
const selectMobileMapControlsOpen   = ({tracking}: TrackingReducerState) => tracking.mobileMapControlsOpen;
const selectFindNearbyResults       = ({tracking: {findNearby}}: TrackingReducerState) => findNearby.results;
const selectGeoFencePanelOpen       = ({tracking}: TrackingReducerState) => tracking.geoFencePanelOpen;
const selectResumeMapStatus         = ({tracking: {maps}}: TrackingReducerState) => maps.resumeMapStatus;
const selectSearchResult            = ({tracking: {search}}: TrackingReducerState) => search.selected;
const selectIsLargeFleetModeLoading = ({tracking: {fleetMode}}: TrackingReducerState) => fleetMode.loading;
const selectIsLargeFleetMode        = ({tracking: {fleetMode}}: TrackingReducerState) => fleetMode.isLargeFleetMode;
const selectAssetsAggregation       = ({tracking: {fleetMode}}: TrackingReducerState) => fleetMode.assetsAggregation;
const selectLargeFleetExtent        = ({tracking: {fleetMode}}: TrackingReducerState) => fleetMode.largeFleetExtent;
const selectLargeFleetRefreshMap    = ({tracking: {fleetMode}}: TrackingReducerState) => fleetMode.refreshMap;
const selectSelectedGroups          = ({tracking: {filters: {groups}}}: TrackingReducerState) => groups.selected;
const selectQueryParams             = ({query: {queryParams}}: QueryReducerState) => queryParams;
const selectDiscToolTagReport       = ({tracking: {tools}}: TrackingReducerState) => tools.disconnectedToolTagReport;
const selectShowMapScaleWidget      = ({tracking: {featureFlags}}) => featureFlags.showMapScaleWidget;
const selectAssetCategories         = ({tracking: {filters: {assetCategories}}}) => assetCategories.items;
const selectSelectedAssetTrailer    = ({tracking: {assets: {smarterTrailer}}}) => smarterTrailer.selectedAssetTrailer;
const selectSmartTrailerLoading     = ({tracking: {assets: {smarterTrailer}}}) => smarterTrailer.loading;
const selectSmarterTrailerError     = ({tracking: {assets: {smarterTrailer}}}) => smarterTrailer.error;
const selectSelectedAssetCategories = ({tracking: {filters: {assetCategories}}}) => assetCategories.selected;
const selectGridColumnState         = ({tracking: {grid}}: TrackingReducerState) => grid.columnState;
const selectGridFilterModel         = ({tracking: {grid}}: TrackingReducerState) => grid.filterModel;
const selectGridState               = ({tracking: {grid}}: TrackingReducerState) => grid.state;
const selectFleetUpdateFlag         = ({tracking: {v2: {fleetUpdateFlag}}}: TrackingReducerState) => fleetUpdateFlag;
const selectFleetUpdateResetFlag    = ({tracking: {v2: {fleetUpdateResetFlag}}}) => fleetUpdateResetFlag;
const selectFleetUpdateFetchingKeys = ({tracking: {v2: {fleetUpdateFetchingKeys}}}: TrackingReducerState): string[] =>
  fleetUpdateFetchingKeys;
const selectGridFilteredAssetIds    = ({tracking: {grid}}: TrackingReducerState) => grid.filteredAssetIds;
const selectAssetDetailsLoading     = ({tracking: {assets}}: TrackingReducerState) => (assets.assetDetailsLoading);
const selectAssetsLoading           = ({tracking: {assets}}: TrackingReducerState) => assets.assetsLoading;
const selectAssetsCount             = ({tracking: {assets}}: TrackingReducerState) => assets.assetsCount;
const selectAssetsInitialExtent     = ({tracking: {assets}}: TrackingReducerState) => assets.initialExtent;
const selectSelectedRoutes          = ({tracking: {filters: {routes}}}) => routes.selected;
const selectIsFiltersActive         = ({tracking}: TrackingReducerState) => tracking.isSelectedFiltersActive;
const selectSelectedAssetStatus     = ({tracking: {filters: {assetStatus}}}) => assetStatus.selected;
const appliedSavedFilters           = ({tracking: {appliedSaveFilter}}: TrackingReducerState) => appliedSaveFilter;
const selectLargeFleetGridCount     = ({tracking: {grid}}: TrackingReducerState) => grid.count;
const selectSavedMapViews           = ({tracking: {mapViews}}: TrackingReducerState) => mapViews.savedMapViews;
const selectShowHistoryRefreshMessage = ({tracking: {history}}: TrackingReducerState) => history.showRefreshMessage;
const selectTrackingAssetsSortDescending = ({tracking: {assets: {isAssetsSortDescending}}}: TrackingReducerState) =>
  isAssetsSortDescending;

const selectSCDevices              = ({tracking: {supplyChainDevices}}: TrackingReducerState) =>
  supplyChainDevices.devices;
const selectSCDevicesUpdates       = ({tracking: {supplyChainDevices}}: TrackingReducerState) =>
  supplyChainDevices.updates;
const selectIsSCDevicesLabelActive = ({tracking: {supplyChainDevices}}: TrackingReducerState) =>
  supplyChainDevices.isLabelActive;
const selectSCDevicesLabelContent  = ({tracking: {supplyChainDevices}}: TrackingReducerState) =>
  supplyChainDevices.labelContent;
const selectSCDevicesLoaded        = ({tracking: {supplyChainDevices}}: TrackingReducerState) =>
  supplyChainDevices.isLoaded;

const selectHoveredAsset                      = (
  {tracking: {assets: {hoveredAsset}}}: TrackingReducerState
) => hoveredAsset.asset;
const selectDevicesTrailerSensor              = (
  {tracking: {assets: {smarterTrailer}}}: TrackingReducerState
) => smarterTrailer.devicesHaveTrailerSensors;
const selectSelectedAssetTypes                = (
  {tracking: {filters: {assetTypes}}}: TrackingReducerState
) => assetTypes.selected;
const selectSmarterTrailerLoading             = (
  {tracking: {assets: {smarterTrailer}}}: TrackingReducerState
) => smarterTrailer.loading;
const selectAssociatedAssetsIsLoading         = (
  {tracking: {assets: {associatedAssets}}}: TrackingReducerState
) => associatedAssets.isLoading;
const selectAssociatedAssets                  = (
  {tracking: {assets: {associatedAssets}}}: TrackingReducerState
) => associatedAssets?.assets;
const selectSensorUpdateLoading               = (
  {tracking: {assets: {smarterTrailer}}}: TrackingReducerState
) => smarterTrailer.sensorUpdateLoading;
const selectLargeFleetAssetCount              = (
  {tracking: {fleetMode: {largeFleetAssetCount}}}: TrackingReducerState
) => largeFleetAssetCount;
const selectLargeFleetAssets                  = (
  {tracking: {fleetMode: {largeFleetAssetList}}}: TrackingReducerState
) => largeFleetAssetList.assets;
const selectHasNextPage                       = (
  {tracking: {fleetMode: {largeFleetAssetList}}}: TrackingReducerState
) => largeFleetAssetList.hasNextPage;
const selectIsNextPageLoading                 = (
  {tracking: {fleetMode: {largeFleetAssetList: {isNextPageLoading}}}}: TrackingReducerState
) => isNextPageLoading;
const selectLargeFleetRefresh                 = (
  {tracking: {fleetMode: {largeFleetAssetList: {refresh}}}}: TrackingReducerState
) => refresh;
const selectRouteHistory                      = (
  {tracking: {trackingHistory}}: TrackingReducerState
) => trackingHistory.routeHistory;
const selectAssetDetailHistoryGridColumnState = (
  {tracking: {assetDetailHistoryGrid: {grid}}}: TrackingReducerState
) => grid.columnState;
const selectAssetDetailHistoryLoading         = (
  {tracking: {assetDetailHistoryGrid: {history}}}: TrackingReducerState
) => history.loading;
const selectAssetDetailHistory                = (
  {tracking: {assetDetailHistoryGrid: {history}}}: TrackingReducerState
) => history.history;
const selectAssetDetailHistoryEventFilter     = (
  {tracking: {assetDetailHistoryGrid: {history}}}: TrackingReducerState
) => history.eventFilter;
const selectHoveredAssetDetailHistoryEvent    = (
  {tracking: {assetDetailHistoryGrid: {history}}}: TrackingReducerState
) => history.hoveredEvent;

const selectEditableDevice = (
  {tracking: {supplyChainDevices: {deviceToEdit}}}: TrackingReducerState
) => deviceToEdit;

const selectAssetClickType = (
  {tracking: {assets: {assetSelectionType}}}: TrackingReducerState
) => assetSelectionType;

const selectIsToolsLoading = (
  {tracking: {tools: toolsLoading}}: TrackingReducerState
) => toolsLoading;

const selectIsFindNearByLoading = (
  {tracking: {findNearby: {isLoading}}}: TrackingReducerState
) => isLoading;

const selectAssetsUpdates = (
  {tracking: {assets: {updates}}}: TrackingReducerState
) => updates;

const selectTrackingMode = ({tracking: {trackingMode}}: TrackingReducerState) => trackingMode;

const selectUpdateGrid       = (
  {tracking: {fleetMode: {largeFleetAssetList: {updateGrid}}}}: TrackingReducerState
) => updateGrid;

const selectAssignedAssetsPanelsOpen = ({tracking: {assignedAssets}}: TrackingReducerState) =>
  assignedAssets.assignedAssetsPanelsOpen;

const selectMapWidgetSelected  = ({tracking}: TrackingReducerState) => tracking.mapWidgetSelected;

const selectLiveHistoryInitialAssets = ({tracking: {historyV2}}: TrackingReducerState) =>
  historyV2.initialAssets;

const selectLiveHistorySelectedAsset = ({tracking: {historyV2}}: TrackingReducerState) =>
  historyV2.selectedAsset;

const selectLiveHistoryActive = ({tracking: {historyV2}}: TrackingReducerState) =>
  historyV2.isActive;

const selectLiveHistoryDisplayReport = ({tracking: {historyV2}}: TrackingReducerState) =>
  historyV2.displayReport;

const selectLiveHistoryRings = ({tracking: {historyV2}}: TrackingReducerState) =>
  historyV2.rings;

const selectLiveHistoryAssetSearchActive =  ({tracking: {historyV2}}: TrackingReducerState) =>
  historyV2.assetsSearchActive;

const selectLiveHistoryAssetSearchList = ({tracking: {historyV2}}: TrackingReducerState) =>
  historyV2.assetSearchList;

const selectLiveHistoryReportType = ({tracking: {historyV2}}: TrackingReducerState) =>
  historyV2.reportType;

const selectLiveHistorySearchTime = ({tracking: {historyV2}}: TrackingReducerState) =>
  historyV2.intervalInHours;

const selectLiveHistoryInputEvents = ({tracking: {historyV2}}: TrackingReducerState) =>
  historyV2.inputEvents;

const selectLiveHistoryError = ({tracking: {historyV2}}: TrackingReducerState) =>
  historyV2.error;

const selectLiveHistoryIsFetching = ({tracking: {historyV2}}: TrackingReducerState) =>
  historyV2.isFetching;

const selectLiveHistoryIsUIFetching = ({tracking: {historyV2}}: TrackingReducerState) =>
  historyV2.isUIFetching;

const selectLiveHistoryIsIOProfileFetching = ({tracking: {historyV2}}: TrackingReducerState) =>
  historyV2.isIOProfileFetching;

const selectLiveHistoryPersistActive = ({tracking: {historyV2}}: TrackingReducerState) =>
  historyV2.persistLiveHistory;

const selectLiveHistoryHideNonSelectedAssetsActive = ({tracking: {historyV2}}: TrackingReducerState) =>
  historyV2.hideNonSelectedAssetsActive;

const selectFleetLayerPersistenceActive = ({tracking: {maps}}: TrackingReducerState) =>
  maps.isFleetLayerPersistenceActive;

const selectTimeMode = ({tracking: {timeMode}}: TrackingReducerState) => timeMode;

export const timeModeSelector = createSelector(selectTimeMode,
  (timeMode: TimeModes): TimeModes => timeMode
);

const selectTimelinePreset = ({tracking: {timelinePreset}}: TrackingReducerState) =>
  timelinePreset;

const selectTimelineDuration = ({tracking: {timelineDuration}}: TrackingReducerState) =>
  timelineDuration;

const selectHistoryPreset = ({tracking: {historyPreset}}: TrackingReducerState) =>
  historyPreset;

const selectHistoryFrom = ({tracking: {historyFrom}}: TrackingReducerState) =>
  historyFrom;

const selectHistoryTo = ({tracking: {historyTo}}: TrackingReducerState) =>
  historyTo;

const selectHistoryActive = ({tracking: {isHistoryActive}}: TrackingReducerState) =>
  isHistoryActive;

const selectIsTimeModePanelOpen = ({tracking: {isTimeModePanelOpen}}: TrackingReducerState) =>
  isTimeModePanelOpen;

export const isTimeModePanelOpenSelector = createSelector(selectIsTimeModePanelOpen,
  (isOpen: boolean): boolean => isOpen
);

export const timelinePresetSelector = createSelector(selectTimelinePreset,
  (preset: number | null): number | null => preset
);

export const timelineDurationSelector = createSelector(selectTimelineDuration,
  (duration: number): number => duration
);

export const historyPresetSelector = createSelector(selectHistoryPreset,
  (preset: DateSelectorValues): DateSelectorValues => preset
);

export const historyFromSelector = createSelector(selectHistoryFrom,
  (from: Date | null): Date | null => from
);

export const historyToSelector = createSelector(selectHistoryTo,
  (to: Date | null): Date | null => to
);

export const historyActiveSelector = createSelector(selectHistoryActive,
  (isActive: boolean): boolean => isActive
);

// Asset position snapping to road
const selectIsTrackingAssetSnapToRoadEnabled = (
  {tracking: {isSnapToRoadEnabled}}: TrackingReducerState) => isSnapToRoadEnabled;
export const isTrackingAssetSnapToRoadEnabledSelector = createSelector(
  selectIsTrackingAssetSnapToRoadEnabled, isSnapToRoadEnabled => isSnapToRoadEnabled);

// Asset Updates Play/Pause
const selectTrackingAssetUpdatesEnabled = (
  {tracking: {isAssetUpdatesEnabled}}: TrackingReducerState) => isAssetUpdatesEnabled;
export const trackingAssetUpdatesEnabledSelector = createSelector(
  selectTrackingAssetUpdatesEnabled, isAssetUpdatesEnabled => isAssetUpdatesEnabled);

// context menu starts...
const selectContextMenuLocation   = ({tracking: {contextMenu}}: TrackingReducerState) => contextMenu?.location;
const selectContextMenuVisibility = ({tracking: {contextMenu}}: TrackingReducerState) => contextMenu?.visibility;
const selectBoundaryMode          = ({tracking: {boundaryMode}}: TrackingReducerState) => boundaryMode;

export const contextMenuLocationSelector   = createDeepEqualSelector(selectContextMenuLocation, location => location);
export const contextMenuVisibilitySelector = createSelector(selectContextMenuVisibility, visibility => visibility);
export const boundaryModeSelector          = createSelector(selectBoundaryMode, mode => mode);
// context menu ends...

export const fleetLayerPersistenceActiveSelector = createSelector(selectFleetLayerPersistenceActive,
  (isFleetLayerPersistenceActive: boolean): boolean => isFleetLayerPersistenceActive
);

// output selectors ...
export const mapWidgetSelectedSelector = createSelector(selectMapWidgetSelected,
  (mapWidgetSelected: string): string => mapWidgetSelected
);

export const liveHistoryInitialAssetsSelector = createDeepEqualSelector(selectLiveHistoryInitialAssets,
  (initialAssets: any[]): any[] => initialAssets
);

export const liveHistoryHideNonSelectedAssetsActive = createSelector(selectLiveHistoryHideNonSelectedAssetsActive,
  (hideNonSelectedAssetsActive: boolean): boolean => hideNonSelectedAssetsActive
);

export const liveHistoryPersistActiveSelector = createSelector(selectLiveHistoryPersistActive,
  (persistLiveHistory: boolean): boolean => persistLiveHistory
);

export const liveHistoryActiveSelector = createSelector(selectLiveHistoryActive,
  (isActive: boolean): boolean => isActive
);

export const liveHistorySelectedAssetSelector = createSelector(selectLiveHistorySelectedAsset,
  (selectedAsset: CVSAssetType): CVSAssetType => selectedAsset
);

export const liveHistoryDisplayReportSelector = createSelector(selectLiveHistoryDisplayReport,
  (displayReport: boolean): boolean => displayReport
);

export const liveHistoryRingsSelector = createSelector(selectLiveHistoryRings,
  (rings: number[]): number[] => rings
);

export const liveHistoryAssetSearchActiveSelector = createSelector(selectLiveHistoryAssetSearchActive,
  (assetsSearchActive: boolean): boolean => assetsSearchActive
);

export const liveHistoryAssetSearchListSelector = createDeepEqualSelector(selectLiveHistoryAssetSearchList,
  (assetSearchList: any[]): any[] => assetSearchList
);

export const liveHistoryReportTypeSelector = createSelector(selectLiveHistoryReportType,
  (reportType: HistoryReportTypeEnum): HistoryReportTypeEnum => reportType
);

export const liveHistorySearchTimeSelector = createSelector(selectLiveHistorySearchTime,
  (intervalInHours: number): number => intervalInHours
);

export const liveHistoryInputEventsSelector = createDeepEqualSelector(selectLiveHistoryInputEvents,
  (inputEvents: any[]): any[] => inputEvents
);

export const liveHistoryErrorSelector = createDeepEqualSelector(selectLiveHistoryError,
  (error: any): any => error
);

export const liveHistoryIsFetchingSelector = createSelector(selectLiveHistoryIsFetching,
  (isFetching: boolean): boolean => isFetching
);

export const liveHistoryIsUIFetchingSelector = createSelector(selectLiveHistoryIsUIFetching,
  (isUIFetching: boolean): boolean => isUIFetching
);

export const liveHistoryIsIOProfileFetchingSelector = createSelector(selectLiveHistoryIsIOProfileFetching,
  (isIOProfileFetching: boolean): boolean => isIOProfileFetching
);

export const isTrackingInitializedSelector = createSelector(selectIsInitialized,
  (isInitialized: boolean): boolean => isInitialized
);

export const bottomGridHeightSelector = createDeepEqualSelector(selectTrackingPersistView,
  (persistView: GenericObject): number => persistView?.bottomGridHeight
);

export const trackingFiltersSelector = createDeepEqualSelector(selectFilters,
  (filters: TrackingFilters): TrackingFilters => filters
);

export const processedFiltersSelector = createDeepEqualSelector(selectProcessedFilters,
  (processedFilters: Array<object>): Array<object> => processedFilters
);

export const searchResultsSelector = createDeepEqualSelector(selectSearchResults,
  (results: any): GlobalSearchResults => results
);

export const searchHistorySelector = createDeepEqualSelector(selectSearchHistory,
  (history: GlobalSearchResult[]): GlobalSearchResult[] => history
);

// the legacy theme's tracking search shows only the 3 most recent of the
// (up to 5) saved searches ...
export const LEGACY_VISIBLE_SEARCH_HISTORY = 3;

export const legacySearchHistorySelector = createDeepEqualSelector(selectSearchHistory,
  (history: GlobalSearchResult[]): GlobalSearchResult[] => history?.slice(0, LEGACY_VISIBLE_SEARCH_HISTORY)
);

export const searchLoadingSelector = createSelector(selectSearchLoading,
  (loading): boolean => loading
);

export const searchSelectedSelector = createDeepEqualSelector(selectSearchSelected,
  (selected: GlobalSearchResult): GlobalSearchResult => selected
);

export const assetsSelector = createDeepEqualSelector(selectAssets,
  (assets) => assets
);

export const findButtonStateSelector = createSelector(selectFindButtonState,
  (findButtonState): boolean => findButtonState
);

export const findNearbySelector = createSelector(selectFindNearby,
  (findNearby: FindNearby): FindNearby => findNearby
);

export const findNearbyIsActiveSelector = createSelector(selectFindNearby,
  (findNearby: FindNearby): boolean => findNearby.isActive
);

export const findNearbyV2IsActiveSelector = createSelector(selectFindNearbyV2,
  (findNearbyV2: FindNearbyV2): boolean => findNearbyV2.isActive
);

export const findNearbyV2RefreshSelector = createSelector(selectFindNearbyV2,
  (findNearbyV2: FindNearbyV2): boolean => findNearbyV2.refresh
);

export const findNearbyV2Selector = createSelector(selectFindNearbyV2,
  (findNearbyV2: FindNearbyV2): FindNearbyV2 => findNearbyV2
);

export const findNearbyV2SelectedAssetSelector = createDeepEqualSelector(selectFindNearbyV2,
  (findNearbyV2: FindNearbyV2): any => findNearbyV2.selectedAsset
);

export const fleetModeSelector = createSelector(selectFleetMode,
  (fleetMode) => fleetMode
);

export const findNearByStateSelector = createSelector(selectFindNearbyState,
  (findNearbyState) => findNearbyState
);

export const mapsSelector = createDeepEqualSelector(selectMaps,
  (maps) => maps
);

export const mapInitializedSelector = createSelector(
  [mapsSelector],
  (maps) => maps.initialized
);

export const selectedMapAssets = createDeepEqualSelector(selectMapAssets,
  (assets: Device[]): Device[] => assets
);

export const smarterTrailerLoadingSelector = createSelector(selectSmarterTrailerLoading,
  (loading: boolean): boolean => loading
);

export const sensorUpdateLoadingSelector = createSelector(selectSensorUpdateLoading,
  (loading: boolean): boolean => loading
);

export const largeFleetAssetCountSelector = createSelector(selectLargeFleetAssetCount,
  (largeFleetAssetCount: number): number => largeFleetAssetCount
);

export const largeFleetAssetsSelector = createSelector(selectLargeFleetAssets,
  (largeFleetAssets: Device[]): Device[] => largeFleetAssets
);

export const toolPanelOpenSelector = createSelector(selectToolPanelOpen,
  (toolPanelOpen: {
    assetId: string | null;
    open: boolean;
  }[]): {
    assetId: string | null;
    open: boolean;
  }[] => toolPanelOpen
);

export const assignedAssetsPanelsOpenSelector = createSelector(selectAssignedAssetsPanelsOpen,
  (assignedAssetsPanelsOpen: string): string => assignedAssetsPanelsOpen
);

export const assetDetailViewSelector = createSelector(
  selectAssetDetailView,
  selectFindNearby,
  (assetDetailView: boolean, findLabel): boolean => assetDetailView && !findLabel?.results?.length
);

export const accountMapSelector = createSelector(selectAccountMap,
  (isMapSelection: AccountHashMapKeyType): AccountHashMapKeyType => isMapSelection
);

export const showPopupFindNearbyStateSelector = createSelector(
  [findNearByStateSelector],
  (findNearbyState) => findNearbyState.showPopup
);

export const geofenceFindNearbySelector = createSelector(
  [findNearbySelector],
  (findNearby) => findNearby.geofence
);
export const locationFindNearbySelector = createSelector(
  [findNearbySelector],
  (findNearby) => findNearby.location
);
export const typeFindNearbySelector = createSelector(
  [findNearbySelector],
  (findNearby) => findNearby.type
);

export const mapAssetsSelector = createSelector(
  [assetsSelector],
  (assets) => assets.assets
);

export const groupsFiltersSelector = createSelector(selectFilters,
  selectFilterType('groups')
);

export const assetTypesFiltersSelector = createSelector(selectFilters,
  selectFilterType('assetTypes')
);

export const assetCategoriesFiltersSelector = createSelector(selectFilters,
  selectFilterType('assetCategories')
);

export const geoFencesFiltersSelector = createSelector(selectFilters,
  selectFilterType('geoFences')
);

export const routesFiltersSelector = createSelector(selectFilters,
  selectFilterType('routes')
);

export const assetStatusFiltersSelector = createSelector(selectFilters,
  selectFilterType('assetStatus')
);

export const boundaryFilterSelector = createSelector(selectFilters,
  selectFilterType('boundary')
);

export const isMobileFilterActiveSelector = createSelector(selectIsMobileFilterActive,
  (isMobileFilterActive: boolean): boolean => isMobileFilterActive
);

export const associatedAssetsSelector = createSelector(selectAssociatedAssets,
  (assets: Array<object>): Array<object> => assets
);

export const assetDetailsLoadingSelector = createSelector(selectAssetDetailsLoading,
  (assetDetailsLoading: boolean): boolean => assetDetailsLoading
);

export const associatedAssetsIsLoadingSelector = createSelector(selectAssociatedAssetsIsLoading,
  (isLoading: boolean): boolean => isLoading
);

export const hasNextPageSelector = createSelector(selectHasNextPage,
  (hasNextPage: boolean): boolean => hasNextPage
);

export const nextPageLoadingSelector = createSelector(selectIsNextPageLoading,
  (isNextPageLoading: boolean): boolean => isNextPageLoading
);

export const largeFleetAssetRefreshSelector = createSelector(selectLargeFleetRefresh,
  (largeFleetAssetRefresh: boolean): boolean => largeFleetAssetRefresh
);

export const findNearbyAssetsSelector = createSelector(selectFindNearbyAssets,
  (results: Array<Device> | []): Array<Device> | [] => results);

export const showPopupSelector = createSelector(selectShowPopup,
  (showPopup: boolean): boolean => showPopup
);

export const findNearbyToggleSelector = createSelector(selectFindNearbyToggle,
  (toggle: boolean): boolean => toggle
);

export const mapLayerSelector = createSelector(selectMapLayers,
  (layers: string[]): string[] => layers
);

export const trafficSubLayersSelector = createDeepEqualSelector(selectTrafficSubLayers,
  (selectTrafficSubLayers: string[]): string[] => selectTrafficSubLayers
);

export const trafficOpacitySelector = createSelector(selectTrafficOpacity,
  (trafficOpacity: number): number => trafficOpacity
);

export const weatherOpacitySelector = createSelector(selectWeatherOpacity,
  (weatherOpacity: number): number => weatherOpacity
);

export const geofenceOpacitySelector = createSelector(selectGeofenceOpacity,
  (geofenceOpacity: number): number => geofenceOpacity
);

export const geofenceBorderOnlySelector = createSelector(selectGeofenceBorderOnly,
  (borderOnly: boolean): boolean => borderOnly
);

export const geofenceLabelSelector = createSelector(selectGeofenceLabel,
  (labelState: boolean): boolean => labelState
);

export const selectedAssetSelector = createDeepEqualSelector(selectSelectedAsset,
  (selectedAsset: Device | null | undefined): Device => selectedAsset
);

export const mapSelectedAssetSelector = createDeepEqualSelector(
  selectSelectedAsset,
  selectFindNearbyV2,
  selectLiveHistorySelectedAsset,
  // eslint-disable-next-line max-params
  (selectedAsset, findNearbyV2, liveHistorySelectedAsset) => {
    if (liveHistorySelectedAsset) {
      return liveHistorySelectedAsset;
    }
    if (findNearbyV2.isPanelActive) {
      return findNearbyV2.selectedAsset;
    }
    return selectedAsset;
  }
);

export const lastSelectedAssetSelector = createDeepEqualSelector(selectLastSelectedAsset,
  (lastSelectedAsset: Device | null | undefined): Device => lastSelectedAsset
);

export const panelLeftPositionSelector = createSelector(selectPanelTopPosition,
  (position: number): number => position
);

export const isAutoRefreshSelector = createSelector(selectIsAutoRefresh,
  (isAutoRefresh: boolean): boolean => isAutoRefresh
);

export const hasGridUpdateSelector = createSelector(selectHasGridUpdate,
  (hasUpdate: boolean): boolean => hasUpdate
);

export const isClusteringV2ActiveSelector = createSelector(selectIsClusteringActive, selectIsMapClusteringActive,
  (clustering: boolean, mapClustering: boolean): boolean => !isNil(mapClustering) ? mapClustering : clustering
);

export const isClusteringActiveSelector = createSelector(selectIsClusteringActive,
  (clustering: boolean): boolean => clustering
);

export const isLabellingActiveSelector = createSelector(selectIsLabellingActive,
  (labelling: boolean): boolean => labelling
);

export const assetLabelContentSelector = createSelector(selectAssetLabelContent,
  (labelContent: LabelContentType): LabelContentType => labelContent
);

export const selectedToolSelector = createDeepEqualSelector(selectSelectedTool,
  (selectedTool: ToolAssetType): ToolAssetType => selectedTool
);

export const toolDetailViewSelector = createSelector(selectToolDetailView,
  (toolDetailView: boolean): boolean => toolDetailView
);

export const hoveredAssetSelector = createDeepEqualSelector(selectHoveredAsset,
  (asset: Device): Device => asset
);

export const trackingAssetsSelector = createDeepEqualSelector(selectMapAssets,
  (assets: Device[]): Device[] => assets
);

export const isMapSelectionSelector = createSelector(selectIsMapSelection,
  (isMapSelection: boolean): boolean => isMapSelection
);

export const panMapOnlySelector = createSelector(selectPanMapOnly,
  (panMapOnly: boolean): boolean => panMapOnly
);

export const assetCategoriesSelector = createDeepEqualSelector(selectAssetCategories,
  (items: any[]): any[] => items
);

export const showMapScaleWidgetSelector = createSelector(selectShowMapScaleWidget,
  (showMapScaleWidget: boolean): boolean => showMapScaleWidget
);

export const selectedGeoFencesSelector = createDeepEqualSelector(selectSelectedGeoFences,
  (selected: any[]): any[] => selected
);

export const savingGeofenceSelector = createSelector(selectSavingGeofence,
  (savingGeofence: boolean): boolean => savingGeofence
);

export const savedGeofenceSelector = createSelector(selectSavedGeofence,
  (savedGeofence: any): any => savedGeofence
);

export const assetsHistorySelector = createDeepEqualSelector(selectAssetsHistory,
  (assets: HVSRecord[]): HVSRecord[] => assets
);

export const historyIntervalInHoursSelector = createSelector(selectHistoryIntervalInHours,
  (intervalInHours: number): number => intervalInHours
);

export const assetsHistoryLoadingSelector = createSelector(selectAssetsHistoryLoading,
  (loading: boolean): boolean => loading
);

export const historyUpdatesLiveSelector = createSelector(selectHistoryUpdatesLive,
  (updatesLive: boolean): boolean => updatesLive
);

export const historyReportTypeSelector = createDeepEqualSelector(selectHistoryReportType,
  (reportType: HistoryReportTypeEnum): HistoryReportTypeEnum => reportType
);

export const historyInputEventsSelector = createDeepEqualSelector(selectHistoryInputEvents,
  (inputEvents: HistoryInputEvents[]): HistoryInputEvents[] => inputEvents
);

export const showHistoryRefreshMessageSelector = createSelector(selectShowHistoryRefreshMessage,
  (showRefreshMessage: boolean): boolean => showRefreshMessage
);

export const selectedHistoryAssetSelector = createDeepEqualSelector(selectSelectedHistoryAsset,
  (selectedAsset: HVSRecord): HVSRecord => selectedAsset
);

export const showHistoryErrorSelector = createSelector(selectShowHistoryError,
  (show: boolean): boolean => show
);

export const historyAssetCountSelector = createSelector(selectHistoryAssetCount,
  (assetCount: number): number => assetCount
);

export const isHistoryRefreshSelector = createSelector(selectIsHistoryRefresh,
  (isRefresh: boolean): boolean => isRefresh
);

export const geofenceStatusSelector = createDeepEqualSelector(selectGeofenceStatus,
  (geofenceStatus: GeoFenceStatus): GeoFenceStatus => geofenceStatus
);

export const initializedSelector = createSelector(selectInitialized,
  (initialized: boolean): boolean => initialized
);

export const isAssetDetailHistoryMapSelector = createSelector(selectIsAssetDetailHistoryMap,
  (isAssetDetailHistory: boolean): boolean => isAssetDetailHistory
);

export const mapBasemapSelector = createDeepEqualSelector(selectBasemap,
  (basemap: BaseMapEnum): BaseMapEnum => basemap
);

export const mapZoomSelector = createSelector(selectZoom,
  (zoom: number): number => zoom
);

export const mapViewSelector = createDeepEqualSelector(selectView,
  (view: __esri.View): __esri.View => view
);

export const mapInitialExtentSelector = createDeepEqualSelector(selectInitialExtent,
  (initialExtent: any): any => initialExtent
);

export const mapExtentSelector = createDeepEqualSelector(selectExtent,
  (extent: any): any => extent
);

export const mapviewInitialExtentSelector = createDeepEqualSelector(selectMapviewInitialExtent,
  (initialExtent: any): any => initialExtent
);

export const mapviewExtentSelector = createDeepEqualSelector(selectMapviewExtent,
  (extent: any): any => extent
);

export const mapCenterSelector = createDeepEqualSelector(selectCenter,
  (center: any): any => center
);

export const operationalLayersSelector = createDeepEqualSelector(selectOperationalLayers,
  (operationalLayers: Array<Object>): Array<Object> => operationalLayers
);

export const isLayerListUpdateSelector = createSelector(selectIsLayerListUpdate,
  (isLayerListUpdate: boolean): boolean => isLayerListUpdate
);

export const islayerVisibilitySelector = createSelector(selectIslayerVisibility,
  (islayerVisibilityUpdate: boolean): boolean => islayerVisibilityUpdate
);

export const mapLayerTypeSelector = createDeepEqualSelector(selectMapLayerType,
  (mapLayerType: MapLayerTypeEnum[]): MapLayerTypeEnum[] => mapLayerType
);

export const mapLayerSettingsSelector = createDeepEqualSelector(selectMapLayerSettings,
  (mapLayerSettings: MapLayerSettings[]): MapLayerSettings[] => mapLayerSettings
);

export const historySearchSelector = createDeepEqualSelector(selectHistorySearch,
  (searchHistory: SavedPlacesProps[]): SavedPlacesProps[] => searchHistory
);

export const mobileMapControlsOpenSelector = createSelector(selectMobileMapControlsOpen,
  (mobileMapControlsOpen: boolean): boolean => mobileMapControlsOpen
);

export const findNearbyResultsSelector = createDeepEqualSelector(selectFindNearbyResults,
  (results: Device[]): Device[] => results
);

export const geoFencePanelOpenSelector = createSelector(selectGeoFencePanelOpen,
  (geoFencePanelOpen: boolean): boolean => geoFencePanelOpen
);

export const resumeMapStatusSelector = createSelector(selectResumeMapStatus,
  (resumeMapStatus: boolean): boolean => resumeMapStatus
);

export const searchResultSelector = createDeepEqualSelector(selectSearchResult,
  (selected: GlobalSearchResult): GlobalSearchResult => selected
);

export const isLargeFleetModeSelector = createSelector(selectIsLargeFleetMode, selectFindNearby,
  (isLargeFleetMode: boolean, findNearby: FindNearby): boolean => isLargeFleetMode && !findNearby.isActive
);
export const isLargeFleetModeLoadingSelector = createSelector(selectIsLargeFleetModeLoading,
  (isLargeFleetModeLoading: boolean): boolean => isLargeFleetModeLoading
);

export const largeFleetRefreshSelector = createSelector(selectLargeFleetRefresh,
  (refresh: boolean): boolean => refresh
);

export const trackinglargeFleetAssetsSelector = createDeepEqualSelector(selectLargeFleetAssets,
  (assets: Device[]): Device[] => assets
);

export const assetsAggregationSelector = createDeepEqualSelector(selectAssetsAggregation,
  (assetsAggregation: CVSAssetAggregations): CVSAssetAggregations => assetsAggregation
);

export const largeFleetExtentSelector = createDeepEqualSelector(selectLargeFleetExtent,
  (largeFleetExtent: object): object => largeFleetExtent
);

export const largeFleetRefreshMapSelector = createSelector(selectLargeFleetRefreshMap,
  (refreshMap: boolean): boolean => refreshMap
);

export const selectedGroupsSelector = createDeepEqualSelector(selectSelectedGroups,
  (selected: any[]): any[] => selected
);

export const selectedAssetTypesSelector = createDeepEqualSelector(selectSelectedAssetTypes,
  (selected: any[]): any[] => selected
);

export const sideSheetViewSelector = createSelector(selectSideSheetView,
  (sideSheetView: SideSheetView): SideSheetView => sideSheetView
);

export const isMapOnlyViewSelector = createSelector(selectIsMapOnlyView,
  (isMapOnlyView: boolean): boolean => isMapOnlyView
);

export const queryParamsSelector = createDeepEqualSelector(selectQueryParams,
  (queryParams: GenericObject): GenericObject => queryParams
);

export const queryParamsDateSelector = createDeepEqualSelector(selectQueryParams,
  (queryParams: GenericObject): GenericObject => queryParams?.date
);

export const disconnectedToolTagReportSelector = createDeepEqualSelector(selectDiscToolTagReport,
  (disconnectedToolTagReport: TagReportType): TagReportType => disconnectedToolTagReport
);

export const selectedAssetCategoriesSelector = createDeepEqualSelector(selectSelectedAssetCategories,
  (selected: any[]): any[] => selected
);

export const gridColumnStateSelector = createDeepEqualSelector(selectGridColumnState,
  (columnState: ColumnState[]): ColumnState[] => columnState
);

export const gridFilterModelSelector = createDeepEqualSelector(selectGridFilterModel,
  (filterModel: FilterModel): FilterModel => filterModel
);

export const trackingGridStateSelector = createDeepEqualSelector(selectGridState,
  (state: GenericObject): GenericObject => state
);

export const trackingFleetUpdateFlagSelector = createSelector(selectFleetUpdateFlag,
  (state: boolean): boolean => state
);

export const trackingFleetUpdateResetFlagSelector = createSelector(selectFleetUpdateResetFlag,
  (state: boolean): boolean => state
);

export const trackingFleetUpdateAnyFetchingSelector = createSelector(selectFleetUpdateFetchingKeys,
  (keys: string[]): boolean => keys.length > 0
);

export const gridFilteredAssetIdsSelector = createDeepEqualSelector(selectGridFilteredAssetIds,
  (filteredAssetIds: number[]): number[] => filteredAssetIds
);

export const selectedRoutesSelector = createDeepEqualSelector(selectSelectedRoutes,
  (selected: any[]): any[] => selected
);

export const selectedAssetStatusSelector = createDeepEqualSelector(selectSelectedAssetStatus,
  (selected: any[]): any[] => selected
);

export const selectedAssetTrailerSelector = createDeepEqualSelector(selectSelectedAssetTrailer,
  (selectedAssetTrailer: AugmentedSmarterTrailerSensorData): AugmentedSmarterTrailerSensorData => selectedAssetTrailer
);

export const smartTrailerLoadingSelector = createSelector(selectSmartTrailerLoading,
  (loading: boolean): boolean => loading
);

export const devicesHaveTrailerSensorsSelector = createDeepEqualSelector(selectDevicesTrailerSensor,
  (devicesHaveTrailerSensors: any): any => devicesHaveTrailerSensors
);

export const smarterTrailerErrorSelector = createDeepEqualSelector(selectSmarterTrailerError,
  (error: any): any => error
);

export const assetsLoadingSelector = createSelector(selectAssetsLoading,
  (assetsLoading: boolean): boolean => assetsLoading
);

export const assetsCountSelector = createSelector(selectAssetsCount,
  (assetsCount: number): number => assetsCount
);

export const largeFleetGridCountSelector = createSelector(selectLargeFleetGridCount,
  (count: number): number => count
);

export const assetsInitialExtentSelector = createDeepEqualSelector(selectAssetsInitialExtent,
  (initialExtent: any): any => initialExtent
);

export const routeHistorySelector = createDeepEqualSelector(selectRouteHistory,
  (routeHistory: GenericObject): GenericObject => routeHistory
);

export const assetDetailHistoryGridColumnStateSelector = createDeepEqualSelector(
  selectAssetDetailHistoryGridColumnState,
  (columnState: ColumnState[]): ColumnState[] => columnState
);

export const assetDetailHistoryLoadingSelector = createSelector(selectAssetDetailHistoryLoading,
  (loading: boolean): boolean => loading
);

export const assetDetailHistorySelector = createDeepEqualSelector(selectAssetDetailHistory,
  (history: any[]): any[] => history
);

export const assetDetailHistoryEventFilterSelector = createDeepEqualSelector(selectAssetDetailHistoryEventFilter,
  (eventFilter: EventCode[]): EventCode[] => eventFilter
);

export const hoveredAssetDetailHistoryEventSelector = createDeepEqualSelector(selectHoveredAssetDetailHistoryEvent,
  (hoveredEvent: HVSRecord): HVSRecord => hoveredEvent
);

export const scDevicesSelector = createDeepEqualSelector(selectSCDevices,
  (devices: Device[]): Device[] => devices
);

export const scDevicesUpdatesSelector = createDeepEqualSelector(selectSCDevicesUpdates,
  (updates: SCDeviceType[]): SCDeviceType[] => updates
);

export const isScDevicesLabelActiveSelector = createSelector(selectIsSCDevicesLabelActive,
  (isLabelActive: boolean): boolean => isLabelActive
);

export const scDevicesLabelContentSelector = createSelector(selectSCDevicesLabelContent,
  (labelContent: LabelContentType): LabelContentType => labelContent
);

export const scDevicesLoadedSelector = createSelector(selectSCDevicesLoaded,
  (isLoaded: boolean): boolean => isLoaded
);

export const appliedSavedFiltersSelector = createDeepEqualSelector(appliedSavedFilters,
  (appliedSavedFilters) => appliedSavedFilters
);

export const savedMapViewsSelector = createDeepEqualSelector(selectSavedMapViews,
  (savedMapViews: SavedMapViewType[]): SavedMapViewType[] => savedMapViews
);

export const editableDeviceSelector = createSelector(selectEditableDevice,
  (device: DeviceEntity) => device
);

export const assetClickTypeSelector = createSelector(selectAssetClickType,
  (type: AssetSelectionType) => type
);

export const showAssetsListSelector = createDeepEqualSelector(
  selectAssetDetailView,
  selectSelectedTool,
  selectAssociatedAssets,
  selectFindNearby,
  // eslint-disable-next-line max-params
  (assetDetailView, selectedTool, associatedAssets, findLabel) =>
    (!assetDetailView && !selectedTool && isArrayEmpty(associatedAssets)) || (findLabel?.miles && findLabel?.type)
);

export const showDetailPanelSelector = createDeepEqualSelector(
  selectSelectedAsset,
  selectAssociatedAssets,
  selectSelectedTool,
  (selectedAsset, associatedAssets, selectedTool) =>
    (selectedAsset || associatedAssets?.length) && !selectedTool
);

export const isFindNearbySelector = createDeepEqualSelector(
  selectFindNearby,
  selectFindNearbyState,
  (findNearBy, findNearByState) => ([findNearBy, findNearByState])
);

export const isToolsLoadingSelector = createSelector(
  selectIsToolsLoading,
  (isToolsLoading) => isToolsLoading
);

export const isFiltersExistSelector = createSelector(selectFilters,
  (filters) => {
    return filters.assetTypes?.selected?.length ||
    filters.assetCategories?.selected?.length ||
    filters.assetStatus?.selected?.length ||
    filters.routes?.selected?.length ||
    filters.groups?.selected?.length ||
    filters.geoFences?.selected.length ||
    filters.boundary.length;
  }
);

export const findNearbyItemSelector = createSelector(selectFindNearby,
  (findNearBy) => ({item: findNearBy.asset || findNearBy.geofence, type: findNearBy.type})
);

export const isFindNearByLoadingSelector = createSelector(selectIsFindNearByLoading,
  (isLoading) => isLoading
);

export const isAsstesCountLoadingSelector = createSelector(selectIsAsstesCountLoading, loading => loading);

export const isAsstesLoadingSelector = createSelector(selectIsAsstesLoading, loading => loading);

export const filteredAssetsSelector = createDeepEqualSelector(selectFilteredAssets, assets => assets || []);

export const filteredDevicesSelector = createDeepEqualSelector(selectFilteredDevices, devices => devices || []);

export const getListAssetsSelector = createDeepEqualSelector(
  selectAssetsList,
  selectSCDevices,
  selectIsAsstesLoading,
  selectIsLargeFleetMode,
  selectLargeFleetAssets,
  selectTrackingAssetsSortDescending,
  // eslint-disable-next-line max-params
  (assets, scDevices, loading, isLargeFleetMode, largeFleetAssets, sortOrder) => {
    if (isLargeFleetMode) {
      let data = [];
      largeFleetAssets && (data = [...data, ...largeFleetAssets]);
      scDevices && (data = [...data, ...scDevices]);
      return {
        includesAssets    : largeFleetAssets?.length > 0,
        includesScDevices : scDevices?.length > 0,
        data
      };
    }
    if (!loading) {
      let data = [];
      assets && (data = [...data, ...assets]);
      scDevices && (data = [...data, ...scDevices]);
      return {
        includesAssets    : assets?.length > 0,
        includesScDevices : scDevices?.length > 0,
        data
      };
    }
    return {
      data : null,
      loading
    };
  }
);

export const trackingAssetsSortDescendingSelector = createSelector(selectTrackingAssetsSortDescending,
  (sortOrder) => sortOrder
);

export const trackingModeSelector = createSelector(selectTrackingMode,
  (trackingMode: TrackingMode): TrackingMode => trackingMode
);

export const isSelectedFiltersActiveSelector = createSelector(selectIsFiltersActive,
  (isFiltersActive: boolean): boolean => isFiltersActive
);

export const isSidesheetOpenSelector = createSelector(
  selectMobileSideSheetOpen,
  selectDesktopSideSheetOpen,
  (isMobileSidesheetOpen: boolean, isDesktopSidesheetOpen: boolean): boolean =>
    isMobileSidesheetOpen || isDesktopSidesheetOpen
);

export const assetsUpdatesSelector = createSelector(selectAssetsUpdates,
  (updates) => updates
);

export const updateGridSelector = createSelector(selectUpdateGrid, (forceUpdateGrid) => forceUpdateGrid);

export const assetsListSelector = createSelector(selectAssetsList, (assetsList) => assetsList);

export const isFMAssetInitializedSelector = createSelector(selectIsFMAssetInitialized,
  (isFMAssetInitialized: boolean): boolean => isFMAssetInitialized
);

export const isGeofenceLoadedSelector = createSelector(selectIsGeofenceLoaded,
  (isGeofenceLoaded: boolean): boolean => isGeofenceLoaded
);
